import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import ProfaseModal from '../../../commons/ProfaseModal';
import PageForm, { FormField } from '../../../commons/PageForm';
import { useAppDispatch, useAppSelector } from '../../../store';
import { getServicioEnGrupo, updateServicioEnGrupo } from '../grupo-empresarial.reducer';
import { proveedorIdFromRow } from '../proveedor/GrupoProveedorTable';
import {
  applyServicioAsignadoFieldChange,
  buildServicioAsignadoBaseFields,
  buildServicioAsignadoSubmitBody,
  categoriaServicioDescripcionFromApi,
  categoriaServicioReadOnlyField,
  idServicioFromApiResponse,
  isCortesiaActiva,
  makeEmptyServicioAsignadoForm,
  parseServicioHistorico,
  prefillServicioAsignadoForm,
  proveedorBloqueadoPorServicioApi,
  servicioAsignadoDisplayValues,
  validateServicioAsignadoForm,
  type ServicioAsignadoCrearFormValues,
  type ServicioHistoricoEntry,
} from './servicio-asignado-form';
import ServicioAsignadoHistoricoPanel from './ServicioAsignadoHistoricoPanel';
import {
  SERVICIO_ASIGNADO_FORM_ID,
  ServicioAsignadoModalActions,
  servicioAsignadoModalBodyGridStyle,
} from './ServicioAsignadoModalLayout';

const ACCENT = '#8b5cf6';

export type { ServicioAsignadoCrearFormValues };

export interface ServicioAsignadoCrearModalProps {
  open: boolean;
  grupoId?: string | null;
  empresaId?: string | null;
  empresaNombre?: string;
  proveedores?: any[];
  empresasGrupo?: any[];
  onClose: () => void;
  onCreated?: () => void;
}

function proveedorOptionsFromRows(rows: any[] | undefined) {
  const seen = new Set<number>();
  const options: { value: string; label: string }[] = [];
  for (const row of rows ?? []) {
    const id = proveedorIdFromRow(row);
    if (id == null || seen.has(id)) continue;
    seen.add(id);
    const label = String(row.proveedor?.nombre ?? row.alias ?? `ID ${id}`).trim();
    options.push({ value: String(id), label: label || `ID ${id}` });
  }
  return options.sort((a, b) => a.label.localeCompare(b.label, 'es'));
}

export default function ServicioAsignadoCrearModal({
  open,
  grupoId,
  empresaId,
  empresaNombre,
  proveedores = [],
  empresasGrupo = [],
  onClose,
  onCreated,
}: ServicioAsignadoCrearModalProps) {
  const dispatch = useAppDispatch();
  const updating = useAppSelector(s => s.grupoEmpresarial.updating);

  const empresaIdNumerico =
    empresaId != null && empresaId !== 'new' ? Number(empresaId) : NaN;
  const defaultSeFacturaAId =
    !Number.isNaN(empresaIdNumerico) ? String(empresaIdNumerico) : '';

  const [form, setForm] = useState<ServicioAsignadoCrearFormValues>(() =>
    makeEmptyServicioAsignadoForm(defaultSeFacturaAId),
  );
  const [errors, setErrors] = useState<
    Partial<Record<keyof ServicioAsignadoCrearFormValues, string>>
  >({});
  const [servicioCargado, setServicioCargado] = useState(false);
  const [cargandoServicio, setCargandoServicio] = useState(false);
  const [proveedorBloqueado, setProveedorBloqueado] = useState(false);
  const [servicioApiId, setServicioApiId] = useState<number | null>(null);
  const [historico, setHistorico] = useState<ServicioHistoricoEntry[]>([]);
  const [categoriaLabel, setCategoriaLabel] = useState('');
  const cargarSeqRef = useRef(0);

  const proveedorOptions = useMemo(
    () => proveedorOptionsFromRows(proveedores),
    [proveedores],
  );

  const empresaOptions = useMemo(
    () =>
      (empresasGrupo ?? [])
        .filter((e: any) => e?.id != null)
        .map((e: any) => ({
          value: String(e.id),
          label: String(e.razonSocial ?? e.nombre ?? `ID ${e.id}`).trim(),
        }))
        .sort((a, b) => a.label.localeCompare(b.label, 'es')),
    [empresasGrupo],
  );

  const proveedorSeleccionado = form.proveedorId.trim() !== '';
  const categoriaUrl =
    proveedorSeleccionado && Number.isFinite(Number(form.proveedorId))
      ? `api/proveedors/${form.proveedorId}/categoria-servicios`
      : '';

  const categoriaField: FormField<ServicioAsignadoCrearFormValues> = servicioCargado
    ? categoriaServicioReadOnlyField()
    : proveedorSeleccionado
      ? {
          name: 'categoriaServicioId',
          label: 'Categoría servicio',
          type: 'autocomplete-on-demand',
          required: true,
          placeholder: 'Buscar categoría por descripción…',
          url: categoriaUrl,
          queryParam: 'descripcion',
          valueKey: 'id',
          labelKey: 'descripcion',
          minChars: 1,
          labelResolver: (_val, raw) =>
            raw?.descripcion != null ? String(raw.descripcion).trim() : undefined,
        }
      : {
          name: 'categoriaServicioId',
          label: 'Categoría servicio',
          type: 'text',
          required: true,
          disabled: true,
          placeholder: 'Seleccione primero un proveedor',
        };

  const cortesiaActiva = isCortesiaActiva(form);

  const displayValues = useMemo(
    () => servicioAsignadoDisplayValues(form, categoriaLabel),
    [form, categoriaLabel],
  );

  const formFields = useMemo<FormField<ServicioAsignadoCrearFormValues>[]>(
    () =>
      buildServicioAsignadoBaseFields({
        proveedorOptions,
        empresaOptions,
        proveedorBloqueado,
        cortesiaActiva,
        categoriaField,
      }),
    [proveedorOptions, empresaOptions, categoriaField, proveedorBloqueado, cortesiaActiva, servicioCargado],
  );

  useLayoutEffect(() => {
    if (!open) return;
    setForm(makeEmptyServicioAsignadoForm(defaultSeFacturaAId));
    setErrors({});
    setServicioCargado(false);
    setCargandoServicio(false);
    setProveedorBloqueado(false);
    setServicioApiId(null);
    setHistorico([]);
    setCategoriaLabel('');
    cargarSeqRef.current += 1;
  }, [open, defaultSeFacturaAId]);

  const cargarServicioPorCategoria = async (categoriaServicioId: string) => {
    const gid = Number(grupoId);
    const eid = empresaIdNumerico;
    const categoriaId = Number(categoriaServicioId);
    if (Number.isNaN(gid) || Number.isNaN(eid) || Number.isNaN(categoriaId)) return;

    const seq = ++cargarSeqRef.current;
    setCargandoServicio(true);
    setServicioCargado(false);
    setProveedorBloqueado(false);
    setServicioApiId(null);
    setHistorico([]);
    setCategoriaLabel('');
    setErrors(prev => ({ ...prev, categoriaServicioId: undefined }));

    try {
      const result = await dispatch(
        getServicioEnGrupo({
          grupoId: gid,
          empresaId: eid,
          servicioId: categoriaId,
        }),
      ).unwrap();
      if (seq !== cargarSeqRef.current) return;
      const data = result.data;
      setServicioCargado(true);
      setProveedorBloqueado(proveedorBloqueadoPorServicioApi(data));
      setServicioApiId(idServicioFromApiResponse(data));
      setHistorico(parseServicioHistorico(data));
      setCategoriaLabel(categoriaServicioDescripcionFromApi(data));
      setForm(prev => prefillServicioAsignadoForm(data, prev));
    } catch {
      if (seq !== cargarSeqRef.current) return;
      setServicioCargado(false);
      setProveedorBloqueado(false);
      setServicioApiId(null);
      setHistorico([]);
      setErrors(prev => ({
        ...prev,
        categoriaServicioId: 'No se pudo cargar el servicio para esta categoría',
      }));
    } finally {
      if (seq === cargarSeqRef.current) setCargandoServicio(false);
    }
  };

  const handleFieldChange = (name: keyof ServicioAsignadoCrearFormValues, value: string) => {
    setErrors(prev => ({ ...prev, [name]: undefined }));
    if (name === 'proveedorId') {
      if (proveedorBloqueado) return;
      cargarSeqRef.current += 1;
      setServicioCargado(false);
      setCargandoServicio(false);
      setServicioApiId(null);
      setHistorico([]);
      setCategoriaLabel('');
      setForm(prev => ({
        ...prev,
        proveedorId: value,
        categoriaServicioId: '',
      }));
      return;
    }
    if (name === 'categoriaServicioId') {
      setForm(prev => ({ ...prev, categoriaServicioId: value }));
      if (value.trim() === '') {
        cargarSeqRef.current += 1;
        setServicioCargado(false);
        setCargandoServicio(false);
        setProveedorBloqueado(false);
        setServicioApiId(null);
        setHistorico([]);
        setCategoriaLabel('');
        return;
      }
      void cargarServicioPorCategoria(value);
      return;
    }
    setForm(prev => applyServicioAsignadoFieldChange(prev, name, value));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validateServicioAsignadoForm(form, {
      servicioCargado: servicioCargado ? undefined : false,
    });
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    const gid = Number(grupoId);
    const eid = Number(empresaId);
    const categoriaId = Number(form.categoriaServicioId);
    if (Number.isNaN(gid) || Number.isNaN(eid) || Number.isNaN(categoriaId)) {
      return;
    }

    const body = buildServicioAsignadoSubmitBody(form);
    const pathServicioId = servicioApiId ?? categoriaId;

    try {
      await dispatch(
        updateServicioEnGrupo({
          grupoId: gid,
          empresaId: eid,
          servicioId: pathServicioId,
          body,
        }),
      ).unwrap();
      onCreated?.();
      onClose();
    } catch {
      /* 403 u otro error: interceptor / UI global */
    }
  };

  if (!open) return null;
  if (grupoId == null || grupoId === 'new' || Number.isNaN(empresaIdNumerico)) return null;

  const subtitle = empresaNombre
    ? `Asigne un servicio a ${empresaNombre}.`
    : 'Asigne un servicio a la empresa seleccionada.';

  return (
    <ProfaseModal
      open
      titleId="servicio-asignado-crear-modal-title"
      size={servicioCargado ? 'large' : 'default'}
      zIndex={100010}
      dialogStyle={servicioCargado ? undefined : { maxWidth: 560, maxHeight: 'min(90vh, 720px)' }}
      onBackdropMouseDown={e => {
        if (e.target === e.currentTarget && !updating) onClose();
      }}
    >
      <div className="d-flex align-items-start justify-content-between gap-3 mb-2">
        <h2 id="servicio-asignado-crear-modal-title" className="profase-modal-title m-0">
          Asignar Servicio
        </h2>
        <button
          type="button"
          className="profase-form-btn-secondary btn-sm flex-shrink-0"
          onClick={onClose}
          disabled={updating}
          aria-label="Cerrar"
        >
          Cerrar
        </button>
      </div>
      <p className="profase-modal-description">{subtitle}</p>
        {proveedorOptions.length === 0 ? (
          <p style={{ margin: '0 0 16px', fontSize: 13, color: '#b45309' }} role="status">
            No hay proveedores asignados a esta empresa. Asigne un proveedor antes de agregar un
            servicio.
          </p>
        ) : (
          <>
            {cargandoServicio ? (
              <p className="m-0 mb-2 text-muted" style={{ fontSize: 12 }}>
                Cargando información del servicio…
              </p>
            ) : null}
            {servicioCargado ? (
              <>
                <div style={servicioAsignadoModalBodyGridStyle()}>
                  <PageForm
                    formId={SERVICIO_ASIGNADO_FORM_ID}
                    fields={formFields}
                    values={displayValues}
                    errors={errors}
                    isNew
                    saving={updating || cargandoServicio}
                    labelInline
                    hideActions
                    onFieldChange={handleFieldChange}
                    onSubmit={handleSubmit}
                    onCancel={onClose}
                    accentColor={ACCENT}
                  />
                  <ServicioAsignadoHistoricoPanel entries={historico} />
                </div>
                <ServicioAsignadoModalActions
                  formId={SERVICIO_ASIGNADO_FORM_ID}
                  updating={updating || cargandoServicio}
                  submitLabel="Guardar"
                  onClose={onClose}
                />
              </>
            ) : (
              <PageForm
                fields={formFields}
                values={form}
                errors={errors}
                isNew
                saving={updating || cargandoServicio}
                labelInline
                onFieldChange={handleFieldChange}
                onSubmit={handleSubmit}
                onCancel={onClose}
                accentColor={ACCENT}
                submitLabel="Guardar"
                cancelLabel="Cancelar"
              />
            )}
          </>
        )}
    </ProfaseModal>
  );
}
